'use client';

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    LVForms?: { render: (id: number, containerId: string) => void };
  }
}

const EMBED_SRC = 'https://crmapi.awarious.com/forms/embed.js';

/** Two-column lead hero: value-prop copy on the left, a boxed CRM form card on
 *  the right. Replaces the hardcoded schedule-a-demo / setup-free-account heroes
 *  with an editable block. The form loads the same way as CrmFormBlock. */
export function LeadFormHeroRenderer({ block }: { block: any }) {
  const formId = Number(block?.formId);
  const containerId = `lv-form-${formId}`;
  const done = useRef(false);

  useEffect(() => {
    if (!formId) return;
    if (!done.current) {
      done.current = true;
      const render = () => window.LVForms?.render(formId, containerId);
      if (window.LVForms) {
        render();
      } else {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`);
        if (existing) {
          existing.addEventListener('load', render);
        } else {
          const script = document.createElement('script');
          script.src = EMBED_SRC;
          script.async = true;
          script.addEventListener('load', render);
          document.head.appendChild(script);
        }
      }
    }
  }, [formId, containerId]);

  const bullets: any[] = block.bullets ?? [];
  const trustItems: any[] = block.trustItems ?? [];

  return (
    <section
      className="relative pt-28 pb-16 overflow-hidden"
      style={{ background: 'var(--t-gradient, linear-gradient(135deg, var(--color-t-primary) 0%, var(--color-t-primary-dark) 100%))' }}
    >
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10 bg-white" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-10 bg-white" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left — copy. On mobile it drops BELOW the form (order-2) so visitors
              see the form first without scrolling; desktop restores left/right.
              DOM order is unchanged, so the <h1> stays first for SEO/screen-readers. */}
          <div className="order-2 lg:order-1 text-white lg:pt-4">
            {block.eyebrow && (
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium mb-6 backdrop-blur-sm">
                {block.eyebrow}
              </div>
            )}
            {block.heading && (
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                {block.heading}
                {block.headingAccent && (
                  <span className="block" style={{ color: 'var(--color-t-cta)' }}>{block.headingAccent}</span>
                )}
              </h1>
            )}
            {block.subheading && (
              <p className="text-lg text-white/80 mb-8 max-w-lg">{block.subheading}</p>
            )}
            {bullets.length > 0 && (
              <ul className="space-y-3 mb-8">
                {bullets.map((b: any, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-white/90">
                    <svg className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--color-t-cta)' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {b.text}
                  </li>
                ))}
              </ul>
            )}
            {block.socialProof && (
              <div className="pt-4 border-t border-white/20 text-sm text-white/80">{block.socialProof}</div>
            )}
          </div>

          {/* Right — form card. First on mobile (order-1), right column on desktop. */}
          <div className="order-1 lg:order-2 lg:pl-8">
            <div className="bg-white rounded-2xl shadow-2xl p-8 relative">
              {block.cardBadge && (
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full px-5 py-1.5 text-sm font-bold text-white shadow-lg whitespace-nowrap"
                  style={{ backgroundColor: 'var(--color-t-cta)' }}
                >
                  {block.cardBadge}
                </div>
              )}
              {block.cardTitle && (
                <h2 className="text-2xl font-bold text-center mt-4 mb-2" style={{ color: 'var(--color-t-heading)' }}>
                  {block.cardTitle}
                </h2>
              )}
              {block.cardSubtitle && (
                <p className="text-center mb-6" style={{ color: 'var(--color-t-body)' }}>{block.cardSubtitle}</p>
              )}

              {formId ? (
                <div id={containerId} className="min-h-[200px]">
                  <div className="animate-pulse space-y-4">
                    <div className="h-10 bg-gray-100 rounded-lg" />
                    <div className="h-10 bg-gray-100 rounded-lg" />
                    <div className="h-10 bg-gray-100 rounded-lg" />
                    <div className="h-12 rounded-lg" style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-cta) 30%, transparent)' }} />
                  </div>
                </div>
              ) : null}

              {trustItems.length > 0 && (
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs" style={{ color: 'var(--color-t-body)' }}>
                    {trustItems.map((t: any, i: number) => (
                      <span key={i} className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {t.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
